/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import { t } from '@sportninja/common/i18n'
import notificationsActions from '@sportninja/common/actions/notifications'
import colors from '@sportninja/common/constants/appColors'
import req from '@sportninja/common/api/request'

import Sheet from '../Sheet'
import { font } from '../css'
import { Flex } from '../Layout'
import { Mobile } from '../Responsive'
import { InviteWrap, Refresh } from './css'
import Text from '../ts-components/Text'
import { Spin, Tooltip } from 'antd'
import FilterBar from './FilterBar'
import NotificationSettingsGearIcon from '../icons/NotificationSettingsGearIcon'
import NotificationSettingsModal from '../NotificationSettingsModal'
import { RefreshIcon, CheckmarkIcon } from './components/icons'
import NotificationItem from './components/NotificationItem'
import SectionHeader from './components/SectionHeader'
import { MarkAllAsReadModal } from './components/ConfirmationModals'

// Import custom hooks and helpers
import { useNotificationsReducer } from './hooks/useNotificationsReducer'
import { useNotificationsApi } from './hooks/useNotificationsApi'
import { groupNotificationsByDate } from './helpers/dateHelpers'

/**
 * Props interface for Notifications component (before Redux connect)
 */
interface NotificationsComponentProps {
  /** Function to toggle the notifications panel */
  toggle: () => void
  /** Function to mark a single notification as read */
  setNotificationAsRead: (id: string) => Promise<void>
  /** Function to mark all notifications as read */
  setAllAsRead: () => Promise<void>
  /** Function to refresh notification count */
  refreshNotificationCount: () => void
  /** Whether the notifications panel is open */
  isOpen: boolean
}

/**
 * Notifications component - displays and manages user notifications
 * Uses useReducer for state management and custom hooks for API operations
 */
const NotificationsComponent = ({
  toggle,
  setNotificationAsRead,
  setAllAsRead,
  refreshNotificationCount,
  isOpen,
}: NotificationsComponentProps) => {
  console.log('🔄 Notifications: Rendering component', { isOpen })

  // State management with custom reducer
  const [state, dispatch] = useNotificationsReducer()

  // API operations hook
  const {
    fetchNotifications,
    fetchMoreNotifications,
    deleteNotification,
    handleFilterChange,
    fetchUnreadCount,
  } = useNotificationsApi(dispatch, state.page, state.filter)

  // Local state for settings modal
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)

  // Local state for mark all as read modal
  const [isMarkAllAsReadModalOpen, setIsMarkAllAsReadModalOpen] =
    useState(false)
  const [isMarkingAllAsRead, setIsMarkingAllAsRead] = useState(false)
  const [markAllAsReadError, setMarkAllAsReadError] = useState<string | null>(
    null
  )

  // Fetch unread count when panel opens
  useEffect(() => {
    if (isOpen === true) {
      fetchUnreadCount()
    }
  }, [isOpen, fetchUnreadCount])

  // Refresh notification count when panel closes
  useEffect(() => {
    if (isOpen === false) {
      refreshNotificationCount()
    }
  }, [isOpen])

  // Initial fetch on mount
  useEffect(() => {
    fetchNotifications(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Handles opening the mark all as read modal
   */
  const handleMarkAllAsReadClick = () => {
    console.log('🔄 Notifications: Mark all as read button clicked')
    setMarkAllAsReadError(null)
    setIsMarkAllAsReadModalOpen(true)
  }

  /**
   * Handles marking all notifications as read from modal confirmation
   */
  const handleMarkAllAsReadConfirm = async () => {
    console.log('🔄 Notifications: Mark all as read confirmed')
    try {
      setIsMarkingAllAsRead(true)
      setMarkAllAsReadError(null)
      dispatch({ type: 'SET_LOADING', payload: true })
      await setAllAsRead()
      await fetchNotifications(1, state.filter)
      await fetchUnreadCount()
      setIsMarkAllAsReadModalOpen(false)
    } catch (error: any) {
      console.error('🚨 Notifications: Error marking all as read', { error })
      setMarkAllAsReadError(error?.message || 'Failed to mark all as read')
    } finally {
      setIsMarkingAllAsRead(false)
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  /**
   * Handles closing the mark all as read modal
   */
  const handleMarkAllAsReadModalClose = () => {
    setIsMarkAllAsReadModalOpen(false)
    setMarkAllAsReadError(null)
  }

  /**
   * Handles notification click - marks as read and closes panel
   */
  const handleNotificationClick = async (notificationId: string) => {
    console.log('🔄 Notifications: Notification clicked', { notificationId })
    await setNotificationAsRead(notificationId)
    toggle()
  }

  /**
   * Handles marking notification as unread
   */
  const handleNotificationMarkAsUnread = async (notificationId: string) => {
    console.log('🔄 Notifications: Marking notification as unread', {
      notificationId,
    })
    try {
      await req(`/notifications/${notificationId}/unread`)
      await fetchNotifications(1, state.filter)
      await fetchUnreadCount()
    } catch (error) {
      console.error('🚨 Notifications: Error marking as unread', { error })
    }
  }

  /**
   * Handles notification deletion
   * Deletes the notification, refreshes the list, and keeps the panel open
   */
  const handleNotificationDelete = async (notificationId: string) => {
    console.log('🔄 Notifications: Deleting notification', { notificationId })
    try {
      await deleteNotification(notificationId)
      // Refresh the notifications list to show updated data
      await fetchNotifications(1, state.filter)
      // Update the unread count badge
      await fetchUnreadCount()
    } catch (error) {
      console.error('🚨 Notifications: Error deleting notification', { error })
      throw error // Re-throw to allow modal to show error
    }
  }

  /**
   * Handles loading more notifications
   */
  const handleLoadMore = async () => {
    console.log('🔄 Notifications: Loading more notifications')
    await fetchMoreNotifications(state.page + 1)
    dispatch({ type: 'SET_PAGE', payload: state.page + 1 })
  }

  /**
   * Handles setting notification open for delete
   */
  const setNotificationOpenForDelete = (notificationId: string | null) => {
    dispatch({
      type: 'SET_NOTIFICATION_OPEN_FOR_DELETE',
      payload: notificationId,
    })
  }

  return (
    <InviteWrap>
      <Flex
        {...({ row: true } as any)}
        alignItems='center'
        css={css`
          margin-bottom: 16px;
          border-bottom: 1px solid ${colors.SECONDARY_200};
          padding-bottom: 8px;
        `}
      >
        <Flex {...({ row: true } as any)} alignItems='center'>
          <Text
            variant='heading5'
            weight='semiBold'
            color={colors.NEUTRAL_0}
            style={`
                margin-right: 10px;
                text-transform: uppercase;
              `}
          >
            Notifications
          </Text>
          <Tooltip title='Refresh' mouseEnterDelay={1} placement='bottom'>
            <button
              onClick={async () => await fetchNotifications(1, state.filter)}
              css={css`
                cursor: pointer;
                justify-content: center;
                align-items: center;
                margin-top: 2px;
              `}
            >
              <RefreshIcon size={20} color={colors.PRIMARY} />
            </button>
          </Tooltip>
          <Tooltip
            title='Notification Settings'
            mouseEnterDelay={1}
            placement='bottom'
          >
            <button
              onClick={() => setIsSettingsModalOpen(true)}
              css={css`
                cursor: pointer;
                display: flex;
                justify-content: center;
                align-items: center;
                margin-left: 8px;
                transition: opacity 0.2s;
                &:hover {
                  opacity: 0.8;
                }
                margin-bottom: 2px;
              `}
            >
              <NotificationSettingsGearIcon size={20} color={colors.PRIMARY} />
            </button>
          </Tooltip>
        </Flex>
        {state.list && state.list?.length > 0 ? (
          <button onClick={handleMarkAllAsReadClick}>
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 4px;
              `}
            >
              <Text
                variant='paragraphXSmall'
                weight='medium'
                color={colors.PRIMARY}
              >
                Mark all as read
              </Text>
              <CheckmarkIcon color={colors.PRIMARY} size={16} />
            </div>
          </button>
        ) : null}
      </Flex>
      <FilterBar
        {...({
          activeFilter: state.filter,
          onFilterChange: handleFilterChange,
          unreadCount: state.unreadCount,
          invitationsUnreadCount: state.invitationsUnreadCount,
        } as any)}
      />
      {state.notificationLoading ? (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            margin-top: 36px;
          `}
        >
          <Spin size='small' />
        </div>
      ) : (
        <ul className='notification-list'>
          {state.list.length > 0 ? (
            <div>
              {(() => {
                const { todayNotifications, earlierNotifications } =
                  groupNotificationsByDate(state.list)

                const hasBothSections =
                  todayNotifications.length > 0 &&
                  earlierNotifications.length > 0

                return (
                  <>
                    {/* TODAY Section */}
                    {todayNotifications.length > 0 && (
                      <>
                        {hasBothSections && <SectionHeader label='Today' />}
                        {todayNotifications.map((item) => (
                          <div key={item.id}>
                            <NotificationItem
                              item={item}
                              setNotificationAsRead={setNotificationAsRead}
                              setNotificationAsUnread={
                                handleNotificationMarkAsUnread
                              }
                              onClick={async () =>
                                handleNotificationClick(item.id)
                              }
                              onPressDelete={async () =>
                                handleNotificationDelete(item.id)
                              }
                              setNotificationOpenForDelete={
                                setNotificationOpenForDelete
                              }
                              notificationOpenForDelete={
                                state.notificationOpenForDelete
                              }
                            />
                            <div
                              css={css`
                                height: 1px;
                                background-color: ${!item?.read_at
                                  ? 'transparent'
                                  : colors.SECONDARY_200};
                                margin-top: ${!item?.read_at ? '4px' : '8px'};
                                margin-bottom: ${!item?.read_at
                                  ? '4px'
                                  : '8px'};
                              `}
                            />
                          </div>
                        ))}
                      </>
                    )}

                    {/* EARLIER Section */}
                    {earlierNotifications.length > 0 && (
                      <>
                        {hasBothSections && <SectionHeader label='Earlier' />}
                        {earlierNotifications.map((item) => (
                          <div key={item.id}>
                            <NotificationItem
                              item={item}
                              setNotificationAsRead={setNotificationAsRead}
                              setNotificationAsUnread={
                                handleNotificationMarkAsUnread
                              }
                              onClick={async () =>
                                handleNotificationClick(item.id)
                              }
                              onPressDelete={async () =>
                                handleNotificationDelete(item.id)
                              }
                              setNotificationOpenForDelete={
                                setNotificationOpenForDelete
                              }
                              notificationOpenForDelete={
                                state.notificationOpenForDelete
                              }
                            />
                            <div
                              css={css`
                                height: 1px;
                                background-color: ${!item?.read_at
                                  ? 'transparent'
                                  : colors.SECONDARY_200};
                                margin-top: ${!item?.read_at ? '4px' : '8px'};
                                margin-bottom: ${!item?.read_at
                                  ? '4px'
                                  : '8px'};
                              `}
                            />
                          </div>
                        ))}
                      </>
                    )}
                  </>
                )
              })()}

              {/* Pagination */}
              {state.pagination && state.pagination.total_pages > state.page ? (
                <div>
                  <button
                    css={css`
                      display: flex;
                      align-self: center;
                      align-items: center;
                      justify-content: center;
                      width: 30%;
                      height: 36px;
                      margin-top: 24px;
                      border: 2px solid ${colors.DEFAULT_FLAIR};
                      border-radius: 8px;
                      color: ${colors.WHITE};
                      ${font.title}
                      font-weight: 700;
                      font-size: 16px;
                      ${state.notificationLoading &&
                      css`
                        cursor: wait;
                      `}
                    `}
                    onClick={handleLoadMore}
                  >
                    Load more
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <li>
              <div
                css={css`
                  text-align: center;
                  margin-top: 56px;
                `}
              >
                {state.notificationError
                  ? (t as any)('errors:anErrorOccurred')
                  : (t as any)('Web:noNotifications')}
              </div>
              <Refresh
                onClick={async () => await fetchNotifications(1, state.filter)}
              >
                {(t as any)('common:refresh')}?
              </Refresh>
            </li>
          )}
        </ul>
      )}

      {/* Notification Settings Modal */}
      <NotificationSettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
      />

      {/* Mark All As Read Confirmation Modal */}
      <MarkAllAsReadModal
        isOpen={isMarkAllAsReadModalOpen}
        onClose={handleMarkAllAsReadModalClose}
        onConfirm={handleMarkAllAsReadConfirm}
        unreadCount={state.unreadCount}
        isLoading={isMarkingAllAsRead}
        error={markAllAsReadError}
      />
    </InviteWrap>
  )
}

const mapStateToProps = () => {
  return {}
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    setNotificationAsRead: bindActionToPromise(
      dispatch,
      notificationsActions.readNotification.request
    ),
    setAllAsRead: bindActionToPromise(
      dispatch,
      notificationsActions.readAll.request
    ),
  }
}

const Notifications = connect(
  mapStateToProps,
  mapDispatchToProps
)(NotificationsComponent)

/**
 * Props interface for NotificationsWrapper component
 */
interface NotificationsWrapperProps {
  /** Custom button component to trigger notifications panel */
  Button: (...args: any[]) => any
  /** Function to refresh notification count */
  refreshNotificationCount?: () => void
}

/**
 * Wrapper component that provides the Sheet container for Notifications
 */
const NotificationsWrapper = ({
  Button,
  refreshNotificationCount = () => {},
}: NotificationsWrapperProps) => {
  console.log('🔄 NotificationsWrapper: Rendering wrapper')

  return (
    <Mobile>
      {(isMobile) => {
        return (
          <Sheet
            {...({
              hideClose: true,
              button: Button,
              position: 'right',
              width: isMobile ? '320px' : '500px',
              css: css`
                .scrolling-container {
                  padding: 16px 16px 16px 16px !important;
                }
                background: ${colors.SECONDARY_300};
              `,
            } as any)}
          >
            {(toggle: any, _: any, isOpen: any) => (
              <Notifications
                toggle={toggle}
                isOpen={isOpen}
                refreshNotificationCount={refreshNotificationCount}
              />
            )}
          </Sheet>
        )
      }}
    </Mobile>
  )
}

NotificationsWrapper.propTypes = {
  Button: PropTypes.func.isRequired,
}

export default NotificationsWrapper
